<template>
  <section class="holiday-workspace">
    <!-- 顶栏操作和过滤框 -->
    <table-toolbar
      :search.sync="search"
      search-placeholder="输入年份等关键字搜索..."
      :show-refresh="true"
      :show-export="true"
      :show-column-settings="true"
      :columns="columnOptions"
      :visible-columns.sync="visibleColumns"
      :default-columns="defaultColumns"
      :fixed-columns="['# ID', '年份', '操作']"
      @refresh="handleReset"
      @export="handleExport"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddHoliday" class="btn-action">新增年份</el-button>
      </template>
    </table-toolbar>

    <!-- 表格主体 -->
    <table-page :loading="loading" :empty="tables.length === 0" :show-top-pagination="false" :show-bottom-pagination="false" @retry="fetchData">
      <el-table
        :data="tables"
        stripe
        style="width: 100%"
        class="premium-table"
      >
        <el-table-column key="col-id" prop="id" label="# ID" width="100">
          <template slot-scope="scope">
            <span class="cell-id">{{ scope.row.id }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-year" prop="year" label="年份" width="120">
          <template slot-scope="scope">
            <el-link type="primary" class="cell-name-link" @click="handleUpdateHoliday(scope.$index, scope.row)">
              {{ scope.row.year }} 年
            </el-link>
          </template>
        </el-table-column>

        <el-table-column key="col-holiday" v-if="visibleColumns.includes('holiday')" prop="holiday" label="节日放假列表" min-width="250">
          <template slot-scope="scope">
            <el-tooltip content="点击编辑节日放假日期" placement="top" :open-delay="400">
            <div class="clickable-cell editable-cell" @click="handleUpdateFestival(scope.$index, scope.row)">
              <span v-if="!scope.row.holiday || festivalsToStr(scope.row.holiday).length === 0" class="text-placeholder">点击配置节假日...</span>
              <div v-else class="tags-wrapper">
                <el-tag
                  v-for="(date, dIdx) in festivalsToStr(scope.row.holiday)"
                  :key="dIdx"
                  size="mini"
                  type="danger"
                  class="holiday-date-tag"
                >
                  {{ date }}
                </el-tag>
              </div>
              <i class="el-icon-edit-outline editable-cell-icon"></i>
            </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column key="col-working" v-if="visibleColumns.includes('working')" prop="working" label="补班工作日列表" min-width="250">
          <template slot-scope="scope">
            <el-tooltip content="点击编辑补班工作日" placement="top" :open-delay="400">
            <div class="clickable-cell editable-cell" @click="handleUpdateWorking(scope.$index, scope.row)">
              <span v-if="!scope.row.working || workingsToStr(scope.row.working).length === 0" class="text-placeholder">点击配置工作日...</span>
              <div v-else class="tags-wrapper">
                <el-tag
                  v-for="(date, dIdx) in workingsToStr(scope.row.working)"
                  :key="dIdx"
                  size="mini"
                  type="success"
                  class="working-date-tag"
                >
                  {{ date }}
                </el-tag>
              </div>
              <i class="el-icon-edit-outline editable-cell-icon"></i>
            </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <!-- 占位自适应列，当所有自定义列都被隐藏时，撑开表格宽度以防止布局和“暂无数据”提示错乱 -->
        <el-table-column v-if="visibleColumns.length === 0" min-width="1"></el-table-column>

        <el-table-column key="col-actions" label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row, scope.$index)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="editYear">
                  <i class="el-icon-edit"></i> 修改年份
                </el-dropdown-item>
                <el-dropdown-item command="editFestival">
                  <i class="el-icon-date"></i> 配置放假
                </el-dropdown-item>
                <el-dropdown-item command="editWorking">
                  <i class="el-icon-time"></i> 配置补班
                </el-dropdown-item>
                <el-dropdown-item command="delete" style="color: #ef4444;">
                  <i class="el-icon-delete" style="color: #ef4444;"></i> 删除记录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </table-page>

    <!-- 弹窗 -->
    <holiday-dialog v-if="holidayDialogVisible" :holiday-data="holidayData" :dialog-visible="holidayDialogVisible" @handleSaveHolidayDialog="handleHolidayDialogChange" @handleCloseHolidayDialog="holidayDialogVisible=false"></holiday-dialog>
    <festival-dialog v-if="festivalDialogVisible" :holiday-data="holidayData" :dialog-visible="festivalDialogVisible" @handleSaveFestivalDialog="handleFestivalDialogChange" @handleCloseFestivalDialog="festivalDialogVisible=false"></festival-dialog>
    <working-dialog v-if="workingDialogVisible" :holiday-data="holidayData" :dialog-visible="workingDialogVisible" @handleSaveWorkingDialog="handleWorkingDialogChange" @handleCloseWorkingDialog="workingDialogVisible=false"></working-dialog>
  </section>
</template>

<script>
import FestivalDialog from './FestivalDialog'
import WorkingDialog from './WorkingDialog'
import HolidayDialog from './HolidayDialog'
import api from '@/api/channel'
import f from '@/filter'
import TableToolbar from '@/components/TableToolbar/index.vue'
import TablePage from '@/components/TablePage/index.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'index',
  components: { FestivalDialog, WorkingDialog, HolidayDialog, TableToolbar, TablePage },
  created () {
    this.fetchData()
  },
  data () {
    return {
      tableData: [],
      search: '',
      visibleColumns: ['holiday', 'working'],
      festivalDialogVisible: false,
      workingDialogVisible: false,
      holidayDialogVisible: false,
      holidayData: {},
      loading: false
    }
  },
  computed: {
    id () {
      return this.$store.getters.id
    },
    tables () {
      const search = this.search.toLowerCase()
      if (search) {
        return this.tableData.filter(data => {
          return Object.keys(data).some(key => {
            return String(data[key]).toLowerCase().indexOf(search) > -1
          })
        })
      }
      return this.tableData
    },
    columnOptions () {
      return [
        { prop: 'holiday', label: '节日列表' },
        { prop: 'working', label: '工作日列表' }
      ]
    },
    defaultColumns () {
      return this.columnOptions.map(item => item.prop)
    }
  },
  watch: {
    id (val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.fetchData()
    }
  },
  methods: {
    fetchData () {
      this.loading = true
      api.getHolidays().then(res => {
        this.tableData = res
      }).finally(() => {
        this.loading = false
      })
    },
    handleReset () {
      this.fetchData()
    },
    // 修改年份
    handleUpdateHoliday (index, rowData) {
      this.holidayData = Object.assign({}, rowData)
      this.holidayDialogVisible = true
    },
    // 修改假日
    handleUpdateFestival (index, rowData) {
      const data = Object.assign({}, rowData)
      this.holidayData = data
      this.festivalDialogVisible = true
    },
    // 修改补班日
    handleUpdateWorking (index, rowData) {
      const data = Object.assign({}, rowData)
      this.holidayData = data
      this.workingDialogVisible = true
    },
    // 新增节假日
    handleAddHoliday () {
      const id = this.tableData.length ? this.tableData[this.tableData.length - 1].id : 0
      this.holidayData = {
        id: id + 1
      }
      this.holidayDialogVisible = true
    },
    // 弹窗回调
    handleHolidayDialogChange (value) {
      if (typeof value === 'object') {
        this.holidayDialogVisible = false
        this.fetchData()
      } else {
        this.holidayDialogVisible = value
      }
    },
    handleFestivalDialogChange (value) {
      this.festivalDialogVisible = value
      this.fetchData()
    },
    handleWorkingDialogChange (value) {
      this.workingDialogVisible = value
      this.fetchData()
    },
    // 转换展示数据
    festivalsToStr (holiday) {
      return holiday ? f.festivalsToStr(holiday) : []
    },
    workingsToStr (working) {
      return working ? f.workingsToStr(working) : []
    },
    // 删除产品
    handleDeleteHoliday (id, i) {
      this.$confirm('确定删除吗?', '提示', { type: 'warning' }).then(() => {
        api.deleteHoliday(id).then(() => {
          this.tableData.splice(i, 1)
          this.$message.success('删除成功')
        })
      })
    },
    handleActionCommand (command, row, index) {
      if (command === 'editYear') {
        this.handleUpdateHoliday(index, row)
      } else if (command === 'editFestival') {
        this.handleUpdateFestival(index, row)
      } else if (command === 'editWorking') {
        this.handleUpdateWorking(index, row)
      } else if (command === 'delete') {
        this.handleDeleteHoliday(row.id, index)
      }
    },
    handleExport () {
      const rows = this.tables.map(row => ({
        ID: row.id,
        年份: row.year,
        节日放假列表: this.festivalsToStr(row.holiday).join(', '),
        补班工作日列表: this.workingsToStr(row.working).join(', ')
      }))
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '节假日')
      XLSX.writeFile(workbook, `节假日-${this.$moment().format('YYYYMMDDHHmmss')}.xlsx`)
    }
  }
}
</script>

<style scoped>
.holiday-workspace {
  padding: 16px 24px;
}

/* 过滤筛选卡片 */
.filter-card {
  margin-top: 0 !important;
  margin-bottom: 20px;
  padding: 16px 24px;
  border-radius: var(--border-radius-card);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-base);
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-left-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-right-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-action {
  flex-shrink: 0;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.btn-reset {
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.btn-reset:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-setting {
  flex-shrink: 0;
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  border-radius: 8px !important;
  height: 32px;
  padding: 0 16px;
}

.btn-setting:hover {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-hover) !important;
  color: var(--text-main) !important;
}

.search-input {
  width: 220px;
  flex-shrink: 0;
}

.search-input :deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border-color);
  height: 32px;
  line-height: 32px;
  font-weight: 500;
  background-color: var(--bg-card);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.search-input :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.search-input :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

/* 表格卡片样式 */
.table-card {
  padding: 20px;
  border-radius: var(--border-radius-card);
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-base);
}

.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  color: var(--text-main) !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right .el-table__fixed-header-wrapper) th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td,
.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

.cell-id,
.cell-text-gray {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-id {
  font-weight: 600;
  color: var(--text-muted);
}

.cell-text-gray {
  color: var(--text-muted);
}

.cell-name-link {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  text-decoration: none !important;
}

/* 日期列表展示 */
.clickable-cell {
  cursor: pointer;
  width: 100%;
  min-height: 28px;
  display: flex;
  align-items: center;
}

.editable-cell {
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.editable-cell:hover {
  border-color: rgba(37, 99, 235, 0.22);
  background: rgba(37, 99, 235, 0.06);
  color: var(--primary-color);
}

.editable-cell-icon {
  margin-left: auto;
  color: var(--text-muted);
  opacity: 0;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.editable-cell:hover .editable-cell-icon {
  color: var(--primary-color);
  opacity: 1;
}

.text-placeholder {
  color: var(--text-muted);
  font-size: 13px;
  font-style: italic;
}

.tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 2px 0;
}

.holiday-date-tag {
  background-color: rgba(239, 68, 68, 0.08) !important;
  border-color: rgba(239, 68, 68, 0.15) !important;
  color: #ef4444 !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
}

.working-date-tag {
  background-color: rgba(16, 185, 129, 0.08) !important;
  border-color: rgba(16, 185, 129, 0.15) !important;
  color: #10b981 !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
}

/* 操作列三点图标 */
.action-btn {
  font-size: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

/* 列配置 Popover 内部样式 */
.popover-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-main);
  padding: 6px 4px;
}

.popover-divider {
  height: 1px;
  background: var(--border-color);
  margin: 6px 0;
}

.popover-body-scroll {
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;
}

.popover-body-scroll::-webkit-scrollbar {
  width: 4px;
}

.popover-body-scroll::-webkit-scrollbar-thumb {
  background: var(--border-hover);
  border-radius: 2px;
}

.popover-body-scroll :deep(.el-checkbox) {
  display: block;
  margin-bottom: 8px;
  margin-right: 0;
}
</style>
